import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { inter, philosopher } from "@/lib/fonts";
import { toast } from "sonner";
import {
  addBankDetailInputSchema,
  IAddBankDetailInput,
} from "muminsmart-utils";
import { useCreateBankDetailMutation } from "@/lib/api/apiSlice";
import { getRtkQueryErrorMessage } from "@/lib/error";

type BankDetailDialogProps = {
  open: boolean;
  walletId?: string;
  onSaved: () => void;
  onDismiss: () => void;
};

export default function BankDetailDialog({
  open,
  walletId,
  onSaved,
  onDismiss,
}: Readonly<BankDetailDialogProps>) {
  const [isSavingBankDetails, setIsSavingBankDetails] = useState(false);
  const [createBankDetail] = useCreateBankDetailMutation();
  const form = useForm<IAddBankDetailInput>({
    resolver: zodResolver(addBankDetailInputSchema),
    defaultValues: {
      accountHolderName: "",
      accountNumber: "",
      ifsc: "",
    },
  });

  const handleSaveBankDetails = async (values: IAddBankDetailInput) => {
    if (!walletId) {
      toast.error("Wallet context is required");
      return;
    }

    setIsSavingBankDetails(true);
    try {
      await createBankDetail({ walletId, data: values }).unwrap();

      form.reset({
        accountHolderName: "",
        accountNumber: "",
        ifsc: "",
      });
      toast.success("Bank details saved successfully");
      onSaved();
    } catch (error: unknown) {
      toast.error(getRtkQueryErrorMessage(error, "Failed to save bank details"));
    } finally {
      setIsSavingBankDetails(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onDismiss();
        }
      }}
    >
      <DialogContent className="rounded-4xl">
        <DialogHeader>
          <DialogTitle className={philosopher.className}>
            Add Bank Account Details
          </DialogTitle>
          <DialogDescription className={inter.className}>
            This seller wallet requires bank details before payout actions.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            className="space-y-3"
            onSubmit={form.handleSubmit(handleSaveBankDetails)}
          >
            <FormField
              control={form.control}
              name="accountHolderName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel
                    className={`${inter.className} text-xs text-gray-600`}
                  >
                    Account Holder Name
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter account holder name"
                      className="mt-1"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="accountNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel
                    className={`${inter.className} text-xs text-gray-600`}
                  >
                    Account Number
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter account number"
                      className="mt-1"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="ifsc"
              render={({ field }) => (
                <FormItem>
                  <FormLabel
                    className={`${inter.className} text-xs text-gray-600`}
                  >
                    IFSC Code
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter IFSC code"
                      className="mt-1"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <p
              className={`text-xs text-red-600 leading-relaxed ${inter.className}`}
            >
              Please check the details before you submit. Once submitted, they
              cannot be updated later.
            </p>

            <div className="mt-2 flex gap-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1 rounded-full"
                onClick={onDismiss}
                disabled={isSavingBankDetails}
              >
                Not Now
              </Button>
              <Button
                type="submit"
                className="flex-1 rounded-full bg-secondary text-white hover:bg-secondary/90"
                disabled={isSavingBankDetails}
              >
                {isSavingBankDetails ? "Saving..." : "Save Bank Details"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
